import { isFsaPlanType } from "@shared/schemas/planTypes";
import { isLifeDisabilityType } from "@shared/schemas/plans";

// Why a plan can't be offered in Open Enrollment yet (null = ready). Inputs
// come from the same loaders the employee wizard prices from, so "ready" means
// employees can be priced. Each check mirrors a $0 path in shared/utils/enrollmentCalculator.ts:
// - rates: no rate rows → "Fallback when no rates". FSA skips it (prices from
//   the employee's election); a composite life/disability plan's rate lives on
//   its coverage setup (ratePerUnit), not in rate rows.
// - coverage amount: life/disability with no setup → "Coverage not
//   configured". Earnings-based amounts also depend on each employee's salary,
//   which can't be checked here — only that the multiple/percentage is set.
// - rating area: rates split by area and the group's area unresolved → "no
//   rating area was resolved" (ratingAreaUnresolvedByPlanId), or resolved but
//   uncovered (ratingAreaGapsByPlanId).
export function openEnrollmentPlanNotReadyReason(input: {
  plan: { id: number | string; type?: string | null };
  ratesByPlanId: Record<number, any[]>;
  contribsByPlanId: Record<number, any[]>;
  ratingAreaGapsByPlanId?: Record<number, unknown>;
  ratingAreaUnresolvedByPlanId?: Record<number, boolean>;
  ldConfigByPlanId?: Record<number, any>;
}): string | null {
  const id = Number(input.plan.id);
  const type = String(input.plan.type || "");
  const isLifeDisability = isLifeDisabilityType(type);
  const ldConfig = input.ldConfigByPlanId?.[id];

  if (input.ratingAreaUnresolvedByPlanId?.[id]) {
    // Shown in the employer portal too: don't say "the client".
    return "This plan is priced by rating area, but the company address on file doesn't match one. Update the company address first";
  }
  if (input.ratingAreaGapsByPlanId?.[id]) {
    return "This plan has no rates for this group's rating area, so employees can't be priced";
  }

  const hasRateRows = input.ratesByPlanId[id]?.length > 0;
  const hasCompositeRate =
    isLifeDisability &&
    !!ldConfig &&
    String(ldConfig.rateType || "composite") === "composite" &&
    Number(ldConfig.ratePerUnit) > 0;
  const missing: string[] = [];
  if (!isFsaPlanType(type) && !hasRateRows && !hasCompositeRate) missing.push("rates");
  if (!(input.contribsByPlanId[id]?.length > 0)) missing.push("contributions");
  if (isLifeDisability && !lifeDisabilityCoverageAmountSet(ldConfig)) missing.push("a coverage amount");
  if (missing.length === 0) return null;

  const list = missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;
  return `${list.charAt(0).toUpperCase()}${list.slice(1)} must be configured before this plan can be included`;
}

/** Same coverage-amount inputs calculateCoverageAmount reads (lifeDisabilityCalculations.ts). */
function lifeDisabilityCoverageAmountSet(config: any): boolean {
  if (!config) return false;
  switch (config.coverageAmountType) {
    case "flat_amount":
      return Number(config.flatCoverageAmount) > 0 || Number(config.minimumCoverage) > 0;
    case "earnings_multiple":
      return Number(config.earningsMultiple) > 0;
    case "earnings_percentage":
      return Number(config.earningsPercentage) > 0;
    default:
      return false;
  }
}
